import { Button, Grid, Step, StepButton, Stepper, Typography } from "@material-ui/core";
import React from "react";
import { withRouter } from "react-router";
import styled from "styled-components";
import GetTextFields from "../../functions/GetTextFields";
import getUrl from "../../functions/getUrl";
import Services from "../../services/Services";
import Theme from "../../Theme";
import WithLoadingSpinner from "../hoc/WithLoadingSpinner";
import { CompanyBackground, CompanyInfo, ContactInfo, emptySignUpFields, IntegrationDetails, ISignUpFields } from "./data/signup-fields";
import SignUpVerificationCodeDialog from "./SignUpVerificationCodeDialog";

const Wrapper = styled(Grid)`
  &&& .MuiFormLabel-asterisk {
    color: ${Theme.colors.error};
  }
  max-width: 100%;
  width: 100%;
`;

const StyledButton = styled(Button)`
  margin-right: 20px;
`;

const ErrorLabel = styled(Typography)`
  width: 100%;
  color: red;
  margin: 4px;
`;

const stepNames: string[] = ["Contact Information", "Company Background", "Integration Details"];

const services: Services = new Services();

function getErrors(errors: string[]) {
  return (
    <Grid item container direction="row" alignItems="flex-start" spacing={2} xs={12}>
      {" "}
      <Grid xs={12} />
      {errors.map(err => (
        <Grid item xl={2} lg={3} md={4} sm={6} xs={12} key={err}>
          <ErrorLabel variant="subtitle2">{err}</ErrorLabel>
        </Grid>
      ))}
    </Grid>
  );
}

function SignUpStepper() {
  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [state, setState] = React.useState<ISignUpFields>(emptySignUpFields);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    let newState: ISignUpFields = {
      ...state,
      [id]: value,
    };

    setState(newState);
  };

  const getFieldValue = (id: string) => {
    return state[id as keyof ISignUpFields];
  };

  const handleNext = () => {
    let nextStep = activeStep + 1;
    setActiveStep(nextStep);
  };

  const handleBack = () => {
    let priorStep = activeStep - 1;
    setActiveStep(priorStep);
  };

  const updateValidationCode = (validationCode: string) => {
    setState({ ...state, validationCode: validationCode });
  };

  async function submitServiceCall() {
    const signupRepo = await services.SignUpRepository();

    try {
      setIsLoading(true);
      let returnedServiceCall = await signupRepo.SubmitServiceCall(state);

      if (returnedServiceCall && returnedServiceCall.includes("error")) {
        throw JSON.parse(returnedServiceCall);
      } else if (returnedServiceCall) {
        window.location.href = getUrl(`/apply-confirmation?scNumber=${returnedServiceCall}`);
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      if (err["errors"] && err["errors"].length) {
        setErrors(err["errors"]);
      } else if (err["Error"] && err["Error"]["Summary"]) {
        setErrors([err["Error"]["Summary"]]);
      } else {
        setErrors([err.toString()]);
      }
    }
  }

  const [showValidationCodeDialog, setShowValidationCodeDialog] = React.useState<boolean>(false);

  return (
    <>
      <SignUpVerificationCodeDialog
        isOpen={showValidationCodeDialog}
        email={state.email}
        validationCode={state.validationCode}
        cancel={() => setShowValidationCodeDialog(false)}
        setFormError={setErrors}
        submitServiceCall={submitServiceCall}
        updateValidationCode={updateValidationCode}
      />
      <WithLoadingSpinner message="Submitting Partner Portal Access Application" isLoading={isLoading}>
        <Wrapper container spacing={2} justify="center">
          <Grid item xs={12}>
            <Stepper nonLinear activeStep={activeStep}>
              {stepNames.map((label, index) => {
                return (
                  <Step key={label}>
                    <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
          {activeStep == 0 && (
            <>
              <Grid item xs={6}>
                {GetTextFields({
                  fields: CompanyInfo,
                  onChange: handleOnChange,
                  getFieldValue,
                })}
              </Grid>
              <Grid item xs={6}>
                {GetTextFields({
                  fields: ContactInfo,
                  onChange: handleOnChange,
                  getFieldValue,
                })}
              </Grid>
            </>
          )}

          {activeStep == 1 && (
            <Grid item xs={12}>
              {GetTextFields({
                fields: CompanyBackground,
                onChange: handleOnChange,
                getFieldValue,
              })}
            </Grid>
          )}

          {activeStep == 2 && (
            <Grid item xs={12}>
              {GetTextFields({
                fields: IntegrationDetails,
                onChange: handleOnChange,
                getFieldValue,
              })}
            </Grid>
          )}

          <Grid item xs={12}>
            {activeStep != 0 && (
              <StyledButton variant="outlined" onClick={handleBack}>
                Back
              </StyledButton>
            )}
            {activeStep != stepNames.length - 1 && (
              <StyledButton variant="outlined" onClick={handleNext}>
                Next
              </StyledButton>
            )}
            {activeStep == stepNames.length - 1 && (
              <StyledButton
                variant="outlined"
                onClick={() => {
                  setShowValidationCodeDialog(true);
                }}
              >
                Apply
              </StyledButton>
            )}
          </Grid>
          {errors && (
            <Grid item xs={12}>
              {getErrors(errors)}
            </Grid>
          )}
        </Wrapper>
      </WithLoadingSpinner>
    </>
  );
}

export default withRouter(SignUpStepper);
